import { useState } from 'react';
import {
  Users,
  Plus,
  MoreVertical,
  UserX,
  UserCheck,
  RefreshCw,
} from 'lucide-react';
import type { AdminUser } from '@yawi/types';
import { Button, Card, Badge } from '@/core/ui/components';
import { useServices, useToast, useConfirmation } from '@/app/providers';
import { useAdminUsers } from '../hooks/useAdminUsers';
import { RoleBadge } from '../components/RoleBadge';
import { AdminFormModal } from '../components/AdminFormModal';

const statusColors: Record<AdminUser['status'], string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  PENDING_VERIFICATION: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  BLOCKED: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  INACTIVE: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
  DELETED: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

const statusLabels: Record<AdminUser['status'], string> = {
  ACTIVE: 'Active',
  PENDING_VERIFICATION: 'Pending verification',
  BLOCKED: 'Blocked',
  INACTIVE: 'Disabled',
  DELETED: 'Deleted',
};

export function AdminsPage() {
  const { adminUserRepository } = useServices();
  const { showToast } = useToast();
  const { confirm } = useConfirmation();
  const { admins, isLoading, error, refresh } = useAdminUsers();

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const handleEdit = (admin: AdminUser) => {
    setEditingAdmin(admin);
    setShowFormModal(true);
    setOpenMenuId(null);
  };

  const handleToggleStatus = async (admin: AdminUser) => {
    setOpenMenuId(null);
    const disabling = admin.status === 'ACTIVE';
    if (disabling) {
      const confirmed = await confirm({
        title: 'Disable Admin User',
        message: `"${admin.name}" will no longer be able to sign in to this panel.`,
        variant: 'warning',
        confirmText: 'Disable',
      });
      if (!confirmed) return;
    }
    try {
      await adminUserRepository.update(admin.id, { status: disabling ? 'INACTIVE' : 'ACTIVE' });
      showToast('success', disabling ? 'Admin user disabled' : 'Admin user enabled');
      refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update status';
      showToast('error', message);
    }
  };

  const formatLastLogin = (date?: string) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();

    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white/90 flex items-center gap-3">
            <Users className="w-7 h-7" />
            Admin Users
          </h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Manage administrators who have access to this panel
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
          >
            Refresh
          </Button>
          <Button
            onClick={() => {
              setEditingAdmin(null);
              setShowFormModal(true);
            }}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Add Admin
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Admins</div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white/90 mt-1">
            {admins.length}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Active</div>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
            {admins.filter((a) => a.status === 'ACTIVE').length}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-500 dark:text-gray-400">Not active</div>
          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
            {admins.filter((a) => a.status !== 'ACTIVE').length}
          </div>
        </Card>
      </div>

      {/* Admin List */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-rose-600 dark:text-rose-400">{error}</p>
            <Button className="mt-4" variant="outline" onClick={refresh}>
              Retry
            </Button>
          </div>
        ) : admins.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 dark:text-white/90">
              No admin users yet
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Add your first admin to get started
            </p>
            <Button
              className="mt-4"
              onClick={() => setShowFormModal(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              Invite Admin
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {admins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                            {admin.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 dark:text-white/90">
                            {admin.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {admin.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <RoleBadge role={admin.role} />
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          statusColors[admin.status]
                        }`}
                      >
                        {statusLabels[admin.status] ?? admin.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {formatLastLogin(admin.lastLoginAt ?? undefined)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="relative">
                        <button
                          onClick={() => setOpenMenuId(openMenuId === admin.id ? null : admin.id)}
                          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>

                        {openMenuId === admin.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenMenuId(null)}
                            />
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-lg z-20 py-1">
                              <button
                                onClick={() => handleEdit(admin)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleToggleStatus(admin)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-2"
                              >
                                {admin.status === 'ACTIVE' ? (
                                  <>
                                    <UserX className="w-4 h-4" />
                                    Disable
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="w-4 h-4" />
                                    Enable
                                  </>
                                )}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Form Modal */}
      {showFormModal && (
        <AdminFormModal
          admin={editingAdmin || undefined}
          onClose={() => {
            setShowFormModal(false);
            setEditingAdmin(null);
          }}
          onSuccess={() => {
            setShowFormModal(false);
            setEditingAdmin(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
